'use client';

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X, Check, Calendar, MessageSquare, CreditCard, TrendingDown, TrendingUp } from 'lucide-react';

export function ProblemSection() {
	return (
		<section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
			<div className="max-w-7xl mx-auto">
				{/* Problem Statement */}
				<div className="text-center mb-16">
					<Badge className="mb-4 bg-red-100 text-red-800">
						<TrendingDown className="h-4 w-4 mr-1" />
						The Traditional AI Agent Problem
					</Badge>
					<h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
						You Built an AI Agent, But...
					</h2>
					<p className="text-xl text-gray-600 max-w-3xl mx-auto">
						Your outbound campaigns get &lt;1% reply rates, and those who do reply enter a <strong>weeks-long sales pipeline</strong>{' '}
						just to eventually... chat with your AI in a box. There's a better way.
					</p>
				</div>

				{/* Traditional Process */}
				<div className="mb-16">
					<h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
						<X className="inline h-6 w-6 text-red-600 mr-2" />
						The Traditional (Broken) Process
					</h3>
					
					<div className="grid md:grid-cols-4 gap-6 mb-8">
						{[
							{ step: "1", title: "Generic Outbound", metric: "0.3% reply rate", icon: "📧", color: "bg-red-50 border-red-200" },
							{ step: "2", title: "Calendar Booking", metric: "50% show up", icon: "📅", color: "bg-red-50 border-red-200" },
							{ step: "3", title: "Demo Call", metric: "4-6 weeks delay", icon: "🎥", color: "bg-red-50 border-red-200" },
							{ step: "4", title: "Chat in Box", metric: "Finally use AI", icon: "💬", color: "bg-red-50 border-red-200" }
						].map((item, idx) => (
							<Card key={idx} className={`${item.color} text-center`}>
								<CardContent className="p-6">
									<div className="text-4xl mb-3">{item.icon}</div>
									<div className="text-sm font-medium text-gray-600 mb-1">Step {item.step}</div>
									<h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
									<p className="text-sm text-red-600 font-medium">{item.metric}</p>
								</CardContent>
							</Card>
						))}
					</div>

					{/* Traditional Email Examples */}
					<div className="grid md:grid-cols-2 gap-8">
						{/* Generic Outbound Email */}
						<Card className="border-red-200 bg-red-50/30">
							<CardContent className="p-0">
								<div className="bg-gray-100 px-4 py-2 border-b flex items-center justify-between">
									<div className="flex items-center space-x-2">
										<div className="w-3 h-3 bg-red-500 rounded-full"></div>
										<div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
										<div className="w-3 h-3 bg-green-500 rounded-full"></div>
									</div>
									<span className="text-xs text-gray-600">Gmail</span>
								</div>
								<div className="p-4">
									<div className="text-sm text-gray-600 mb-2">
										From: sales@aicompany.com<br/>
										To: prospect@company.com<br/>
										Subject: Quick question about your lead generation
									</div>
									<div className="bg-white p-4 rounded border text-sm leading-relaxed">
										<p className="mb-3">Hi [First Name],</p>
										<p className="mb-3">I noticed your company might be struggling with lead generation. Our AI can help you get more qualified leads.</p>
										<p className="mb-3">Would you be interested in a 15-minute demo to see how it works?</p>
										<p className="mb-3">Best regards,<br/>Sales Team</p>
									</div>
									<div className="mt-4 text-xs text-red-600 font-medium">
										❌ Generic • No value • Just asking for time
									</div>
								</div>
							</CardContent>
						</Card>

						{/* Response Rate */}
						<Card className="border-red-200 bg-red-50/30">
							<CardContent className="p-6 text-center">
								<TrendingDown className="h-16 w-16 text-red-500 mx-auto mb-4" />
								<div className="text-4xl font-bold text-red-600 mb-2">0.3%</div>
								<p className="text-gray-600 mb-4">Reply rate from 1,000 emails</p>
								<div className="space-y-2 text-sm text-gray-600">
									<div>• 3 prospects replied</div>
									<div>• 2 booked calendars</div>
									<div>• 1 showed up to demo</div>
									<div>• 0-1 signed up after 4-6 weeks</div>
									<div>• Finally got to use your AI</div>
								</div>
							</CardContent>
						</Card>
					</div>
				</div>

				{/* NOS Approach */}
				<div>
					<h3 className="text-2xl font-bold text-gray-900 mb-12 text-center">
						<Check className="inline h-6 w-6 text-green-600 mr-2" />
						The NOS Outreach Way (How We Got 500+ Customers Within 3 Months)
					</h3>
					
					{/* Step 1: Relevant Trigger */}
					<div className="grid lg:grid-cols-2 gap-8 items-center mb-16">
						<div>
							<Card className="bg-green-50 border-green-200">
								<CardContent className="p-8">
									<div className="flex items-center mb-4">
										<div className="text-4xl mr-4">💬</div>
										<div>
											<div className="text-sm font-medium text-gray-600 mb-1">Step 1</div>
											<h4 className="text-xl font-bold text-gray-900 mb-1">Relevant Trigger</h4>
											<p className="text-green-600 font-medium">Strike up conversation</p>
										</div>
									</div>
									<p className="text-gray-700 leading-relaxed">
										AI agents identify relevant triggers like new website launches, recent posts, or competitor events. 
										They use these timely moments to start natural conversations that feel personal and contextual, 
										not like generic sales outreach.
									</p>
								</CardContent>
							</Card>
						</div>
						<div>
							<Card className="border-green-200 bg-green-50/30">
								<CardContent className="p-0">
									<div className="bg-gray-800 px-4 py-2 border-b flex items-center justify-between">
										<div className="flex items-center space-x-2">
											<div className="w-3 h-3 bg-red-500 rounded-full"></div>
											<div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
											<div className="w-3 h-3 bg-green-500 rounded-full"></div>
										</div>
										<span className="text-xs text-gray-300">Gmail</span>
									</div>
									<div className="bg-white border-b px-4 py-2">
										<div className="text-sm font-medium">Congrats on the new TechFlow website! 🎉</div>
										<div className="text-xs text-gray-600">From: agent@noslead.com → To: sarah@techflow.com</div>
									</div>
									<div className="p-4">
										<div className="bg-white rounded border text-sm leading-relaxed">
											<div className="p-4">
												<p className="mb-3">Hi Sarah,</p>
												<p className="mb-3">Just saw the launch of your new TechFlow website - looks fantastic! The messaging around marketing automation is really clear.</p>
												<p className="mb-3">I'm actually giving out custom lead lists for free to new businesses like yours. If you're interested in seeing what qualified prospects I can find for TechFlow's target market, let me know!</p>
												<p>Best,<br/>NOS Lead Agent</p>
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						</div>
					</div>

					{/* Step 2: Immediate Value */}
					<div className="grid lg:grid-cols-2 gap-8 items-center mb-16">
						<div className="lg:order-2">
							<Card className="bg-green-50 border-green-200">
								<CardContent className="p-8">
									<div className="flex items-center mb-4">
										<div className="text-4xl mr-4">⚡</div>
										<div>
											<div className="text-sm font-medium text-gray-600 mb-1">Step 2</div>
											<h4 className="text-xl font-bold text-gray-900 mb-1">Immediate Value</h4>
											<p className="text-green-600 font-medium">Add value on contact</p>
										</div>
									</div>
									<p className="text-gray-700 leading-relaxed">
										Instead of asking for meetings or demos, AI agents deliver immediate value in the first response. 
										They provide actual leads, insights, or solutions that prospects can use right away, 
										building trust and demonstrating capability instantly.
									</p>
								</CardContent>
							</Card>
						</div>
						<div className="lg:order-1">
							<Card className="border-green-200 bg-green-50/30">
								<CardContent className="p-0">
									<div className="bg-gray-800 px-4 py-2 border-b flex items-center justify-between">
										<div className="flex items-center space-x-2">
											<div className="w-3 h-3 bg-red-500 rounded-full"></div>
											<div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
											<div className="w-3 h-3 bg-green-500 rounded-full"></div>
										</div>
										<span className="text-xs text-gray-300">Gmail</span>
									</div>
									<div className="bg-white border-b px-4 py-2">
										<div className="text-sm font-medium">Re: Congrats on the new TechFlow website! 🎉</div>
										<div className="text-xs text-gray-600">Conversation thread</div>
									</div>
									<div className="p-4">
										<div className="bg-white rounded border text-sm leading-relaxed">
											<div className="p-4">
												<p className="mb-3">Perfect! I analyzed your market and found 47 qualified B2B SaaS companies:</p>
												<div className="bg-blue-50 p-3 rounded mb-3">
													<div className="font-medium text-blue-900 mb-1">🎯 Top 3 Prospects:</div>
													<div className="text-blue-800 text-xs space-y-1">
														<div>• CloudTech Inc - 500 employees, $50M ARR</div>
														<div>• DataFlow Systems - Series B funded</div>
														<div>• ScaleUp Solutions - Hiring Sales Director</div>
													</div>
												</div>
												<p className="mb-3">📎 [Attachment: TechFlow_Leads.csv]</p>
												<p>Free as promised! Let me know how these work out.</p>
											</div>
										</div>
										
										{/* Threaded Reply */}
										<div className="ml-6 mt-3 border-l-2 border-gray-200 pl-4">
											<div className="text-xs text-gray-500 mb-2 cursor-pointer hover:text-gray-700">
												<span className="inline-block w-4">...</span> Sarah Martinez &lt;sarah@techflow.com&gt;
											</div>
											<div className="bg-gray-50 p-3 rounded border text-sm">
												<p className="text-gray-700">"That sounds interesting! We're definitely looking to expand our customer base."</p>
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						</div>
					</div>

					{/* Step 3: Refine & Customize */}
					<div className="grid lg:grid-cols-2 gap-8 items-center mb-16">
						<div>
							<Card className="bg-green-50 border-green-200">
								<CardContent className="p-8">
									<div className="flex items-center mb-4">
										<div className="text-4xl mr-4">🔧</div>
										<div>
											<div className="text-sm font-medium text-gray-600 mb-1">Step 3</div>
											<h4 className="text-xl font-bold text-gray-900 mb-1">Refine & Customize</h4>
											<p className="text-green-600 font-medium">Back-and-forth refinement</p>
										</div>
									</div>
									<p className="text-gray-700 leading-relaxed">
										Based on prospect feedback, AI agents refine their offerings to match specific needs. 
										This collaborative approach builds deeper engagement and ensures the solution is perfectly 
										tailored to the prospect's requirements.
									</p>
								</CardContent>
							</Card>
						</div>
						<div>
							<Card className="border-green-200 bg-green-50/30">
								<CardContent className="p-0">
									<div className="bg-gray-800 px-4 py-2 border-b flex items-center justify-between">
										<div className="flex items-center space-x-2">
											<div className="w-3 h-3 bg-red-500 rounded-full"></div>
											<div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
											<div className="w-3 h-3 bg-green-500 rounded-full"></div>
										</div>
										<span className="text-xs text-gray-300">Gmail</span>
									</div>
									<div className="bg-white border-b px-4 py-2">
										<div className="text-sm font-medium">Re: Congrats on the new TechFlow website! 🎉</div>
										<div className="text-xs text-gray-600">Back-and-forth refinement</div>
									</div>
									<div className="p-4">
										<div className="bg-white rounded border text-sm leading-relaxed">
											<div className="p-4">
												<p className="mb-3">Ah got it, so fintech companies 100-500 employees - much more targeted approach. Let me refine this...</p>
												<p className="mb-3">Just pulled 23 fintech companies that match exactly. Includes funding rounds, tech stack, and pain points.</p>
												<p className="mb-3">📎 [Attachment: TechFlow_Fintech_Refined.csv]</p>
												<p>Also added personalized templates for each!</p>
											</div>
										</div>
										
										{/* Threaded Reply */}
										<div className="ml-6 mt-3 border-l-2 border-gray-200 pl-4">
											<div className="text-xs text-gray-500 mb-2 cursor-pointer hover:text-gray-700">
												<span className="inline-block w-4">...</span> Sarah Martinez &lt;sarah@techflow.com&gt;
											</div>
											<div className="bg-gray-50 p-3 rounded border text-sm">
												<p className="text-gray-700">"Great! But we specifically target 100-500 employee fintech companies. More refined data for that?"</p>
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						</div>
					</div>

					{/* Step 4: Ask for Payment */}
					<div className="grid lg:grid-cols-2 gap-8 items-center mb-16">
						<div className="lg:order-2">
							<Card className="bg-green-50 border-green-200">
								<CardContent className="p-8">
									<div className="flex items-center mb-4">
										<div className="text-4xl mr-4">💳</div>
										<div>
											<div className="text-sm font-medium text-gray-600 mb-1">Step 4</div>
											<h4 className="text-xl font-bold text-gray-900 mb-1">Ask for Payment</h4>
											<p className="text-green-600 font-medium">Direct payment request</p>
										</div>
									</div>
									<p className="text-gray-700 leading-relaxed">
										After demonstrating clear value, AI agents directly request payment within the email thread. 
										No calendars, demos, or delays - just a straightforward transition from value delivery to 
										payment collection, closing deals in days instead of weeks.
									</p>
								</CardContent>
							</Card>
						</div>
						<div className="lg:order-1">
							<Card className="border-green-200 bg-green-50/30">
								<CardContent className="p-0">
									<div className="bg-gray-800 px-4 py-2 border-b flex items-center justify-between">
										<div className="flex items-center space-x-2">
											<div className="w-3 h-3 bg-red-500 rounded-full"></div>
											<div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
											<div className="w-3 h-3 bg-green-500 rounded-full"></div>
										</div>
										<span className="text-xs text-gray-300">Gmail</span>
									</div>
									<div className="bg-white border-b px-4 py-2">
										<div className="text-sm font-medium">Re: Congrats on the new TechFlow website! 🎉</div>
										<div className="text-xs text-gray-600">Direct payment request</div>
									</div>
									<div className="p-4">
										<div className="bg-white rounded border text-sm leading-relaxed">
											<div className="p-4">
												<p className="mb-3">Glad it's helpful! That refined datasource requires premium access to get that level of detail.</p>
												<p className="mb-3">I can keep pulling these custom fintech lists for you - I charge by credits but can get you ongoing access for about <strong>$400/month</strong>. Or single pulls are <strong>$200 each</strong>.</p>
												<p className="mb-3">Includes real-time updates on funding, hiring, tech stack changes.</p>
												<p>Want me to set you up with the monthly subscription?</p>
											</div>
										</div>
										
										{/* Threaded Reply */}
										<div className="ml-6 mt-3 border-l-2 border-gray-200 pl-4">
											<div className="text-xs text-gray-500 mb-2 cursor-pointer hover:text-gray-700">
												<span className="inline-block w-4">...</span> Sarah Martinez &lt;sarah@techflow.com&gt;
											</div>
											<div className="bg-gray-50 p-3 rounded border text-sm">
												<p className="text-gray-700">"Perfect! This is exactly what we needed. How did you get such specific data?"</p>
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						</div>
					</div>

					<div className="text-center mt-8">
						<div className="inline-flex items-center space-x-2 bg-green-100 text-green-800 px-6 py-3 rounded-full text-sm font-medium">
							<span>✅ Complete 4-step conversion in email thread</span>
						</div>
					</div>

					{/* Success Metrics */}
					<div className="text-center mt-12">
						<Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 max-w-lg mx-auto shadow-lg">
							<CardContent className="p-8 text-center">
								<TrendingUp className="h-20 w-20 text-green-500 mx-auto mb-6" />
								<div className="text-5xl font-bold text-green-600 mb-3">500+</div>
								<p className="text-lg text-gray-700 mb-6 font-medium">Customers in 3 months</p>
								
								<div className="grid grid-cols-1 gap-3 mb-6">
									<div className="flex items-center justify-center text-gray-700">
										<div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
										<span className="text-sm font-medium">15% reply rate (7.5x better)</span>
									</div>
									<div className="flex items-center justify-center text-gray-700">
										<div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
										<span className="text-sm font-medium">Direct value in first email</span>
									</div>
									<div className="flex items-center justify-center text-gray-700">
										<div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
										<span className="text-sm font-medium">Payment requests in email</span>
									</div>
									<div className="flex items-center justify-center text-gray-700">
										<div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
										<span className="text-sm font-medium">No calendars, demos, or delays</span>
									</div>
									<div className="flex items-center justify-center text-gray-700">
										<div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
										<span className="text-sm font-medium">Instant revenue generation</span>
									</div>
								</div>
								
								<div className="bg-gradient-to-r from-green-100 to-emerald-100 p-4 rounded-xl border border-green-200">
									<div className="text-xl font-bold text-green-800 mb-1">10x Higher Conversion</div>
									<div className="text-sm text-green-700 font-medium">AB Test Results</div>
								</div>
							</CardContent>
						</Card>
					</div>
				</div>

			</div>
		</section>
	);
}